import { Language } from "@/i18n/languages";
import { FeatureName, ModuleName } from "./product";
import { CompanyItem, UserItem } from "./common";
import { AccessType } from "@/types/enums/AccessType";

export type CustomRoleItem = {
  role: CustomRole;
  users: UserItem[];
  availableUsers: UserItem[];
  companies: CompanyItem[];
};

export type CustomRole = {
  customRoleId: string;
  orgNo: string;
  customRoleName: string;
  modules: ModuleName[];
  features: AccessToFeature[];
  customRoleLanguages: CustomRoleLanguage[];
};

export type CustomRoleLanguage = {
  customRoleId: string;
  language: Language;
  name: string;
  description?: string;
};

export type CustomRoleForUser = {
  customRoleId: string;
  orgNo: string;
  companiesUsingCustomRole: string[];
  customRoleName: string;
  modules: ModuleName[];
  features: FeatureName[];
  allowOverview?: boolean;
  allowShareholderRegister?: boolean;
  allowDbot?: boolean;
  allowArchive?: boolean;
  allowMessaging?: boolean;
  allowOwnershipRegister?: boolean;
  allowSignatures?: boolean;

  //overview
  allowDashboardView?: boolean;
  allowEntityDataEdit?: boolean;
  allowEntityDataView?: boolean;
  allowArticlesEdit?: boolean;
  allowArticlesView?: boolean;
  allowPoAsEdit?: boolean;
  allowPoAsView?: boolean;
  allowOwnershipView?: boolean;
  allowCompanyRolesEdit?: boolean;
  allowCompanyRolesView?: boolean;
  allowCompanySettingsEdit?: boolean;
  allowCompanySettingsView?: boolean;

  //shareholder register
  allowShareholderRegisterDashboardView?: boolean;
  allowShareholdersEdit?: boolean;
  allowShareholdersView?: boolean;
  allowTransactionsEdit?: boolean;
  allowTransactionsView?: boolean;
  allowShareClassEdit?: boolean;
  allowShareClassView?: boolean;
  allowFinancialInstrumentsEdit?: boolean;
  allowFinancialInstrumentsView?: boolean;
  allowCapTableEdit?: boolean;
  allowCapTableView?: boolean;
  allowCommitmentsEdit?: boolean;
  allowCommitmentsView?: boolean;
  allowShareholderRegisterStatementEdit?: boolean;
  allowShareholderRegisterStatementView?: boolean;
  allowBeneficialOwnersEdit?: boolean;
  allowBeneficialOwnersView?: boolean;
  allowSignaturesView?: boolean;

  //dbot

  //ownership register
  allowOwnersEdit?: boolean;
  allowOwnersView?: boolean;
  allowOwnersTransactionsEdit?: boolean;
  allowOwnersTransactionsView?: boolean;
  allowOwnersShareClassesEdit?: boolean;
  allowOwnersShareClassesView?: boolean;

  //archive
  allowBoardArchiveEdit?: boolean;
  allowBoardArchiveView?: boolean;
  allowShareholderArchiveEdit?: boolean;
  allowShareholderArchiveView?: boolean;
  allowAdminArchiveEdit?: boolean;
  allowAdminArchiveView?: boolean;
  allowCustomArchiveEdit?: boolean;
  allowCustomArchiveView?: boolean;
  allowBoardMessagesEdit?: boolean;
  allowBoardMessagesView?: boolean;
  allowShareholderMessagesEdit?: boolean;
  allowShareholderMessagesView?: boolean;
};

export function hasModuleEnabled(role: CustomRole, moduleName: ModuleName): boolean {
  const hasModule = role.modules.includes(moduleName);
  const hasCustomArchiveFeature =
    moduleName !== ModuleName.ARCHIVE ||
    role.features.some((f) => f.featureName === FeatureName.customArchives && f.access !== "NONE");
  return hasModule && hasCustomArchiveFeature;
}

export type FeatureAccess = "MANAGE" | "EDIT" | "VIEW" | "NONE";
export const FeatureAccessValues: FeatureAccess[] = ["MANAGE", "EDIT", "VIEW", "NONE"];

export type AccessToFeature = {
  featureName: FeatureName;
  access: FeatureAccess;
};

export type NewCustomRoleRequest = {
  roleName: string;
  roleLanguages: CustomRoleLanguage[];
  enabledModules: string[];
  featureAccessList: AccessToFeature[];
  companiesUsingRole: string[];
  isShared: boolean;
};

export type UpdateCustomRoleRequest = NewCustomRoleRequest & {
  customRoleId: string;
};

export const allowNone: CustomRoleForUser = {
  orgNo: "none",
  customRoleId: "none",
  customRoleName: "none",
  modules: [],
  companiesUsingCustomRole: [],
  features: [],
};

export const allowAll: CustomRoleForUser = {
  customRoleId: "all",
  customRoleName: "all",
  orgNo: "all",
  companiesUsingCustomRole: [],
  modules: Object.values(ModuleName),
  features: Object.values(FeatureName),
  //modules
  allowOverview: true,
  allowShareholderRegister: true,
  allowDbot: true,
  allowArchive: true,
  allowMessaging: true,
  allowOwnershipRegister: true,

  //overview
  allowDashboardView: true,
  allowEntityDataEdit: true,
  allowEntityDataView: true,
  allowArticlesEdit: true,
  allowArticlesView: true,
  allowPoAsEdit: true,
  allowPoAsView: true,
  allowOwnershipView: true,
  allowCompanyRolesEdit: true,
  allowCompanyRolesView: true,
  allowCompanySettingsEdit: true,
  allowCompanySettingsView: true,

  //shareholder register
  allowShareholderRegisterDashboardView: true,
  allowShareholdersEdit: true,
  allowShareholdersView: true,
  allowTransactionsEdit: true,
  allowTransactionsView: true,
  allowShareClassEdit: true,
  allowShareClassView: true,
  allowFinancialInstrumentsEdit: true,
  allowFinancialInstrumentsView: true,
  allowCapTableEdit: true,
  allowCapTableView: true,
  allowCommitmentsEdit: true,
  allowCommitmentsView: true,
  allowShareholderRegisterStatementEdit: true,
  allowShareholderRegisterStatementView: true,
  allowBeneficialOwnersEdit: true,
  allowBeneficialOwnersView: true,

  //dbot

  //ownership register
  allowOwnersEdit: true,
  allowOwnersView: true,
  allowOwnersTransactionsEdit: true,
  allowOwnersTransactionsView: true,
  allowOwnersShareClassesEdit: true,
  allowOwnersShareClassesView: true,

  //archive
  allowBoardArchiveEdit: true,
  allowBoardArchiveView: true,
  allowShareholderArchiveEdit: true,
  allowShareholderArchiveView: true,
  allowAdminArchiveEdit: true,
  allowAdminArchiveView: true,
  allowCustomArchiveEdit: true,
  allowCustomArchiveView: true,
  allowBoardMessagesEdit: true,
  allowBoardMessagesView: true,
  allowShareholderMessagesEdit: true,
  allowShareholderMessagesView: true,
};

export const mergeRoles = (roles: CustomRoleForUser[], orgNo: string): CustomRoleForUser => {
  const rolesForThisCompany = roles.filter(
    (r) => r.orgNo === orgNo || r.companiesUsingCustomRole.includes(orgNo),
  );
  const modules = rolesForThisCompany.reduce<ModuleName[]>(
    (acc, curr) => [...acc, ...curr.modules],
    [],
  );
  const features = rolesForThisCompany.reduce<FeatureName[]>(
    (acc, curr) => [...acc, ...curr.features],
    [],
  );
  const mergedFeatureFlags = rolesForThisCompany.reduce<CustomRoleForUser>((acc, curr) => {
    return Object.keys({ ...acc, ...curr }).reduce(
      (merged, key) => {
        const prop = key as keyof CustomRoleForUser;
        if (
          (typeof curr[prop] === "boolean" || typeof curr[prop] === "undefined") &&
          (typeof merged[prop] === "boolean" || typeof merged[prop] === "undefined")
        ) {
          (merged[prop] as boolean | undefined) = merged[prop] || curr[prop];
        }
        return merged;
      },
      { ...acc },
    );
  }, allowNone);

  return {
    ...mergedFeatureFlags,
    customRoleId: "merged",
    customRoleName: "Merged",
    modules: Object.values(ModuleName).filter((m) => modules.includes(m)),
    features: Object.values(FeatureName).filter((f) => features.includes(f)),
  };
};

export type CompanyAccess = {
  companies: {
    organisationNumber: string;
    accessType: AccessType;
    portfolioId?: string;
    portfolioName?: string;
  }[];
  userRoles: CustomRoleForUser[];
};

export const roleForCompany = ({ companies, userRoles }: CompanyAccess, orgNo: string) => {
  const accessType = companies.find((c) => c.organisationNumber === orgNo)?.accessType;
  switch (accessType) {
    case AccessType.ADMIN:
      return allowAll;
    case AccessType.CUSTOM:
      return mergeRoles(userRoles, orgNo);
    default:
      return allowNone;
  }
};
